import type { CurrentUser } from "./types";

/**
 * Access matrix (Settings → Roles & Permissions). Three levels, each inheriting from the one above unless it's set:
 * area (e.g. "procurement") → page (a sidebar menu key, e.g. "erp.material-requests") → action
 * ("erp.material-requests:approve"). A person's own setting wins; otherwise the highest any of their roles gives.
 * The server works it all out (apps/api/src/common/accessRegistry.ts) and sends the levels with the signed-in user.
 */
export type AccessLevel = "NONE" | "OWN" | "TEAM" | "DEPARTMENT" | "ALL";
export const ACCESS_LEVELS: AccessLevel[] = ["NONE", "OWN", "TEAM", "DEPARTMENT", "ALL"];
export const ACCESS_LEVEL_LABEL: Record<AccessLevel, string> = { NONE: "None", OWN: "Own", TEAM: "Team", DEPARTMENT: "Department", ALL: "All" };
export const levelRank = (l: string | undefined | null) => Math.max(0, ACCESS_LEVELS.indexOf((l ?? "NONE") as AccessLevel));
/** Yes/No items read better as Yes / No. */
export const levelLabel = (l: string, levels: string[]) => (levels.length === 2 && levels[0] === "NONE" ? (l === "NONE" ? "No" : "Yes") : ACCESS_LEVEL_LABEL[l as AccessLevel] ?? l);

export interface PageRule {
  match: string;
  prefix?: boolean;
  area: string;
  min: AccessLevel;
}
export const findPageRule = (rules: PageRule[] | undefined, page: string) => rules?.find((r) => (r.prefix ? page.startsWith(r.match) : page === r.match)) ?? null;

const isSuper = (user: CurrentUser | null | undefined) => !!user?.roles.includes("SUPER_ADMIN");

/** The signed-in person's level for an area, page or action. */
export function levelOf(user: CurrentUser | null | undefined, key: string): AccessLevel {
  if (!user) return "NONE";
  if (isSuper(user)) return "ALL";
  const levels = user.accessLevels;
  if (levels?.[key]) return levels[key] as AccessLevel;
  if (!key.includes(":")) {
    const rule = findPageRule(user.pageRules, key);
    if (rule && levels?.[rule.area]) return levels[rule.area] as AccessLevel;
  }
  return ((user.areaAccess?.[key] as AccessLevel) ?? "NONE") as AccessLevel;
}
/** May the signed-in person do this action / open this page? (`min` defaults to anything but None.) */
export const canDo = (user: CurrentUser | null | undefined, key: string, min: AccessLevel = "OWN") => levelRank(levelOf(user, key)) >= levelRank(min);

export const areaLevel = (user: CurrentUser | null | undefined, area: string): AccessLevel => (isSuper(user) ? "ALL" : ((user?.areaAccess?.[area] ?? "NONE") as AccessLevel));
export const hasArea = (user: CurrentUser | null | undefined, area: string, min: AccessLevel = "OWN") => levelRank(areaLevel(user, area)) >= levelRank(min);

// ------------------------------------------------------------------- resolving (the matrix page shows inherited values)

export interface AccessRegistry {
  areas: Array<{ key: string; levels: string[] }>;
  pageRules: PageRule[];
  actions: Array<{ key: string; levels: string[] }>;
}

export function makeResolver(reg: AccessRegistry) {
  const areas = new Map(reg.areas.map((a) => [a.key, a]));
  const actions = new Map(reg.actions.map((a) => [a.key, a]));
  const parent = (key: string): string | null => (key.includes(":") ? key.slice(0, key.lastIndexOf(":")) : areas.has(key) ? null : findPageRule(reg.pageRules, key)?.area ?? null);
  const levelsFor = (key: string): string[] => {
    if (key.includes(":")) return actions.get(key)?.levels ?? ["NONE", "ALL"];
    if (areas.has(key)) return areas.get(key)!.levels;
    const rule = findPageRule(reg.pageRules, key);
    return (rule && areas.get(rule.area)?.levels) || ["NONE", "OWN", "TEAM", "ALL"];
  };
  const clamp = (level: string, levels: string[]) => levels.reduce((out, l) => (levelRank(l) <= levelRank(level) && levelRank(l) >= levelRank(out) ? l : out), "NONE");
  const roleLevel = (map: Record<string, string>, key: string): string => {
    if (map[key]) return clamp(map[key], levelsFor(key));
    const p = parent(key);
    return p ? clamp(roleLevel(map, p), levelsFor(key)) : "NONE";
  };
  const userLevel = (map: Record<string, string>, key: string): string | null => {
    if (map[key]) return clamp(map[key], levelsFor(key));
    const p = parent(key);
    const up = p ? userLevel(map, p) : null;
    return up === null ? null : clamp(up, levelsFor(key));
  };
  return {
    parent,
    levelsFor,
    /** what a role has for a key (set or inherited) */
    roleLevel,
    /** what a role would inherit if nothing were set on the key */
    roleInherited: (map: Record<string, string>, key: string) => {
      const p = parent(key);
      return p ? clamp(roleLevel(map, p), levelsFor(key)) : "NONE";
    },
    /** a person's level: their own setting (closest one) wins, otherwise the best of their roles */
    personLevel: (roleMaps: Array<Record<string, string>>, own: Record<string, string>, key: string, superAdmin = false) => {
      const levels = levelsFor(key);
      if (superAdmin) return levels[levels.length - 1];
      const mine = userLevel(own, key);
      if (mine !== null) return mine;
      return roleMaps.reduce((best, m) => {
        const l = roleLevel(m, key);
        return levelRank(l) > levelRank(best) ? l : best;
      }, "NONE");
    },
    /** what a person would have without their own setting on this key */
    personInherited: (roleMaps: Array<Record<string, string>>, own: Record<string, string>, key: string) => {
      const p = parent(key);
      const mine = p ? userLevel(own, p) : null;
      if (mine !== null) return clamp(mine, levelsFor(key));
      return roleMaps.reduce((best, m) => {
        const l = roleLevel(m, key);
        return levelRank(l) > levelRank(best) ? l : best;
      }, "NONE");
    },
  };
}
